import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";
import * as _ from "lodash";
import { CustomerHierarchyContext } from "../PA.Utils/CustomerHierarchyContext";
import * as AsyncLock from "async-lock";
import HierarachyTransformService, { IHierarchyTransformService } from "../PA.MD.Hierarchy/HierarachyTransformService";
import { GenericLease } from "../PA.Utils/GenericLease";
import { EnterpriseUtils } from "../PA.Core/Basic/EnterpriseUtils";
import { LegacyCustomerHierarchyContext } from "../PA.Utils/LegacyCustomerHierarchyContext";
import { IDateRange } from "../PA.Core/PA.Core.IF.Basic";
import { PeriodService } from "./PeriodService";
import { DateUtils } from "../PA.Core/Basic/Dates";

export class CustomerHierarchyService {
    private static dctCustHierContextCache: { [id: string]: CustomerHierarchyContext } = {};
    private static dctLegacyCustHierContextCache: { [id: string]: LegacyCustomerHierarchyContext } = {};
    private static dctLock: AsyncLock = new AsyncLock();
    private static transformService: IHierarchyTransformService = HierarachyTransformService;

    static async getEntityTransformation(
        custHierWbID: number,
        wbSalesOrgId: number,
        sourceWbEntityId: number,
        targetWbEntityId: number,
        sourceWdId: number,
        effectiveDate: Date,
    ): Promise<number[]> {
        try {
            const context = await CustomerHierarchyService.getHierarchyContext(custHierWbID, wbSalesOrgId);
            return context.transform(sourceWbEntityId, targetWbEntityId, sourceWdId, effectiveDate);
        } catch (e: unknown) {
            if (e instanceof Error) {
                throw e;
            }
            throw new Error(`Unknown error ${JSON.stringify(e)}`);
        }
    }

    private static async getHierarchyContext(
        custHierWbID: number,
        wbSalesOrgId: number,
    ): Promise<CustomerHierarchyContext> {
        return CustomerHierarchyService.dctLock.acquire("global", async () => {
            if (custHierWbID in CustomerHierarchyService.dctCustHierContextCache) {
                return CustomerHierarchyService.dctCustHierContextCache[custHierWbID];
            }

            const lease = new GenericLease(LeaseRegistry.Lease_Subject_Customer_Hierarchy);
            lease.addClasses(CustomerHierarchyService.getLeaseClasses(custHierWbID));
            const salesOrg = await EnterpriseUtils.getSalesOrg(wbSalesOrgId);
            if (!salesOrg) {
                throw new Error(`Sales Org with WB ID ${wbSalesOrgId} not found`);
            }
            const company = await EnterpriseUtils.getCompany(salesOrg.cif.WB_COMPANY_ID);
            if (!company) {
                throw new Error(`Unable to find company for sales org ${salesOrg.cif.SALESORG_ID}`);
            }

            const context = new CustomerHierarchyContext(
                custHierWbID,
                wbSalesOrgId,
                company.cif.WB_MDSET_ID,
                lease,
                CustomerHierarchyService.transformService,
            );
            CustomerHierarchyService.dctCustHierContextCache[custHierWbID] = context;

            return context;
        });
    }

    static getLeaseClasses(custHierWbID: number): string[] {
        const retVal: string[] = [];
        retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Customer_Hierarchy, custHierWbID));
        return retVal;
    }

    /******************************************************************************************
     *
     *
     *
     * Legacy Customer Hierarchy
     *
     *
     *
     * *****************************************************************************************/
    /** @deprecated use new CustomerHierarchyContext that uses the enterprise hierarchy */
    private static async getLegacyHierarchyContext(custHierWbID: number): Promise<LegacyCustomerHierarchyContext> {
        return CustomerHierarchyService.dctLock.acquire("global", async () => {
            let context: LegacyCustomerHierarchyContext;

            if (custHierWbID in CustomerHierarchyService.dctLegacyCustHierContextCache) {
                context = CustomerHierarchyService.dctLegacyCustHierContextCache[custHierWbID];
            } else {
                context = new LegacyCustomerHierarchyContext(custHierWbID);
                CustomerHierarchyService.dctLegacyCustHierContextCache[custHierWbID] = context;
            }

            return context;
        });
    }

    /** @deprecated use new methods that use the enterprise hierarchy */
    static getWbIDsByCustomerType(custHierWbID: number, customerType: string): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            CustomerHierarchyService.getLegacyHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getWBIdsByCustomerType(customerType);
                })
                .then((keys) => {
                    resolve(keys);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    /** @deprecated use new methods that use the enterprise hierarchy */
    static getParent(
        custHierWbID: number,
        custWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
        customerType?: string,
        depthLimit?: number,
    ): Promise<number> {
        return new Promise<number>((resolve, error) => {
            CustomerHierarchyService.getLegacyHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getParentWbID(custWbID, effectiveStartDt, effectiveEndDt, customerType, depthLimit);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getAllParents(custHierWbID: number, custWbID: number): Promise<{ wbID: number; dateRange: IDateRange }[]> {
        return new Promise<{ wbID: number; dateRange: IDateRange }[]>((resolve, error) => {
            CustomerHierarchyService.getLegacyHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getAllCustParents(custWbID);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static isCustWbIdInHierarchy(custHierWbID: number, custWbID: number): Promise<boolean> {
        return new Promise<boolean>((resolve, error) => {
            CustomerHierarchyService.getLegacyHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.isCustWbIdInHierarchy(custWbID);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    /** @deprecated use new methods that use the enterprise hierarchy */
    static getChildren(
        custHierWbID: number,
        custWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
        customerType: string,
        depthLimit?: number,
    ): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            CustomerHierarchyService.getLegacyHierarchyContext(custHierWbID)
                .then((context) => {
                    return context.getChildren(custWbID, effectiveStartDt, effectiveEndDt, customerType, depthLimit);
                })
                .then((results) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    /** @deprecated use new methods that use the enterprise hierarchy */
    static getParentByPeriodWbID(
        custHierWbID: number,
        custWbID: number,
        periodWbID: number,
        customerType?: string,
        depthLimit?: number,
    ): Promise<number> {
        return new Promise<number>((resolve, error) => {
            let dates: IDateRange;
            CustomerHierarchyService.getDateRangeFromPeriodWbID(periodWbID)
                .then((dateRange: IDateRange) => {
                    if (_.isUndefined(dateRange)) {
                        resolve(undefined);
                        return;
                    }

                    dates = dateRange;
                    return CustomerHierarchyService.getLegacyHierarchyContext(custHierWbID);
                })
                .then((context) => {
                    return context.getParentWbID(custWbID, dates.StartDt, dates.EndDt, customerType, depthLimit);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    /** @deprecated use new methods that use the enterprise hierarchy */
    static getChildrenByPeriodWbID(
        custHierWbID: number,
        custWbID: number,
        periodWbID: number,
        customerType?: string,
        depthLimit?: number,
    ): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            let dates: IDateRange;
            CustomerHierarchyService.getDateRangeFromPeriodWbID(periodWbID)
                .then((dateRange) => {
                    if (_.isUndefined(dateRange)) {
                        resolve([]);
                        return;
                    }

                    dates = dateRange;
                    return CustomerHierarchyService.getLegacyHierarchyContext(custHierWbID);
                })
                .then((context) => {
                    return context.getChildren(custWbID, dates.StartDt, dates.EndDt, customerType, depthLimit);
                })
                .then((results) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    /** @deprecated use new methods that use the enterprise hierarchy */
    private static getDateRangeFromPeriodWbID(periodWbID: number): Promise<IDateRange> {
        return new Promise<IDateRange>((resolve, error) => {
            PeriodService.getPeriodByWBID(periodWbID)
                .then((result) => {
                    if (!_.isUndefined(result)) {
                        resolve({
                            StartDt: DateUtils.convertFromSAPBW(result.cif.PERIOD_START_DT),
                            EndDt: DateUtils.convertFromSAPBW(result.cif.PERIOD_END_DT),
                        });
                    } else {
                        resolve(undefined);
                    }
                })
                .catch((err) => {
                    error(err);
                });
        });
    }
}
